import { FC, useEffect, useState, useMemo, useContext } from 'react';
import { UserContext, VendorPageContext } from '../../../context';
import { useMediaQuery } from '@mui/material';
import { Table, Pagination, Link } from '../../../components';
import { getInvoices, getInvoiceStatuses, getSubcontractorsTypes, getJobs } from '../../../fetch';
import { IInvoice, IInvoiceStatus } from '../../../models/invoices';
import { InvoicesFilters } from './InvoicesFilters';
import { useSnackbar } from 'notistack';
import { formatDate, formatMoney } from '../../../helpers';
import { InvoicesMobile } from './InvoicesMobile';
import { ISubcontractorType } from '../../../models';
import { VendorPage } from '../VendorPage';

interface IInvoices {}

export const Invoices: FC<IInvoices> = () => {
  const isMobile = useMediaQuery('(max-width: 960px)');
  const { enqueueSnackbar } = useSnackbar();
  const { isAdmin } = useContext(UserContext);
  const { loadingVendors, vendorSageId } = useContext(VendorPageContext);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoadingInvoices, setLoadingInvoices] = useState<boolean>(true);
  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const [selectedSort, setSelectedSort] = useState<string>('dateInvoice');
  const [sortDirection, setSortDirection] = useState<{
    vendor?: 'Asc' | 'Desc';
    dateInvoice?: 'Asc' | 'Desc';
    invoiceNumber?: 'Asc' | 'Desc';
  }>({
    dateInvoice: 'Desc'
  });

  const [searchValue, setSearchValue] = useState('');
  const [selectedSubType, setSelectedSubType] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [filters, setFilters] = useState<{
    jobSageId?: number | string;
    searchValue?: number | string;
    vendorSageId?: number | string;
    subcontractorTypeId?: number | string;
    type?: number | string;
    subType?: number | string;
    status?: string;
  } | null>({});

  const [loadingVendorSubTypes, setLoadingVendorSubTypes] = useState<boolean>(true);
  const [vendorSubTypes, setVendorSubTypes] = useState<ISubcontractorType[]>(null);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(true);
  const [statuses, setStatuses] = useState<IInvoiceStatus[]>(null);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [selectedJobSageId, setSelectedJobSageId] = useState('');
  const [jobName, setJobName] = useState(null);

  const fetchInvoices = async () => {
    try {
      setLoadingInvoices(true);
      const res = await getInvoices({
        page: page + 1,
        perPage: perPage,
        search: searchValue,
        status: selectedStatus,
        vendorSageId: vendorSageId || null,
        jobSageId: parseInt(selectedJobSageId) ? parseInt(selectedJobSageId) : null,
        subcontractorTypeId: selectedSubType,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string
      });
      setRecordCount(res.totalRecordCount);
      setInvoices(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading invoices, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingInvoices(false);
    }
  };

  const fetchVendorSubTypes = async () => {
    try {
      setLoadingVendorSubTypes(true);
      const res = await getSubcontractorsTypes();
      setVendorSubTypes(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading subcontractor types, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingVendorSubTypes(false);
    }
  };

  const fetchInvoiceStatuses = async () => {
    try {
      setIsLoadingStatuses(true);
      const res = await getInvoiceStatuses();
      setStatuses(res);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading invoice statuses, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingStatuses(false);
    }
  };

  const fetchJobs = async () => {
    try {
      setIsLoadingJobs(true);
      const res = await getJobs({ name: jobName });
      setJobs(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading jobs, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingJobs(false);
    }
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };
  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorSageId, filters, sortDirection, selectedSort, page, perPage]);

  useEffect(() => {
    if (isAdmin) {
      fetchVendorSubTypes();
    }
    fetchJobs();
    fetchInvoiceStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const invoicesColumns = useMemo(() => {
    return [
      {
        Header: 'Invoice No.',
        accessor: 'invoiceNumber',
        isServerSorted: selectedSort === 'invoiceNumber',
        isServerSortedDesc: sortDirection.invoiceNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('invoiceNumber'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IInvoice } };
        }) => {
          return <Link to={`/vendors/invoices/${original.recNum}`}>{original.invoiceNumber}</Link>;
        }
      },
      {
        Header: 'Date',
        accessor: '',
        isServerSorted: selectedSort === 'dateInvoice',
        isServerSortedDesc: sortDirection.dateInvoice === 'Desc',
        handleClickColumn: () => handleClickColumn('dateInvoice'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IInvoice } };
        }) => {
          return (
            <span>
              {original.status === 'Open'
                ? `Invoiced ${formatDate(original.dateInvoice)}`
                : original.status === 'Paid'
                ? new Date(original.dateDue) > new Date()
                  ? `Be Paid ${formatDate(original.dateDue)}`
                  : `Paid ${formatDate(original.dateDue)}`
                : original.status === 'Review'
                ? `Submitted ${formatDate(original.dateInvoice)}`
                : `Approved ${formatDate(original.dateInvoice)}`}
            </span>
          );
        }
      },
      {
        Header: 'Job',
        accessor: 'job',
        sort: false
      },
      isAdmin && {
        Header: 'Vendor',
        accessor: 'vendor',
        isServerSorted: selectedSort === 'vendor',
        isServerSortedDesc: sortDirection.vendor === 'Desc',
        handleClickColumn: () => handleClickColumn('vendor')
      },
      isAdmin && {
        Header: 'Subcontractor Type',
        accessor: 'vendorSubType',
        sort: false
      },
      {
        Header: 'Description',
        accessor: 'description',
        sort: false
      },
      {
        Header: 'Last Viewed',
        accessor: 'dateLastViewed',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IInvoice } };
        }) => {
          return <span>{formatDate(original.dateLastViewed)}</span>;
        }
      },
      {
        Header: 'Status',
        accessor: 'status',
        sort: false
      },
      {
        Header: 'Check No.',
        accessor: 'payments',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IInvoice } };
        }) => {
          return (
            <>
              {original.payments &&
                original.payments.map(checkNumber => {
                  const isACH = checkNumber.includes('ACH ');
                  return (
                    <Link
                      key={checkNumber}
                      to={`/vendors/payments/${checkNumber}?type=${
                        isACH ? 'ACHPayment' : 'Check'
                      }&vendorId=${original.vendorId}`}
                    >
                      {checkNumber}
                    </Link>
                  );
                })}
            </>
          );
        }
      },
      {
        Header: 'Total',
        accessor: 'total',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IInvoice } };
        }) => {
          return <span>{formatMoney(original.total)}</span>;
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);

  return (
    <VendorPage>
      <InvoicesFilters
        jobs={jobs}
        setSelectedJobSageId={setSelectedJobSageId}
        selectedJobSageId={selectedJobSageId}
        isSubcontractorSelected={typeof vendorSageId === 'number'}
        isAdmin={isAdmin}
        isLoading={isLoadingInvoices || loadingVendors || isLoadingJobs}
        handleSearch={(clearSearch?: boolean) => {
          setPage(0);
          setFilters({
            subType: selectedSubType,
            status: selectedStatus as string,
            searchValue: searchValue
          });
          if (clearSearch) {
            setSearchValue('');
          }
        }}
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          if (clearFilters) {
            setFilters({
              vendorSageId: '',
              type: '',
              subType: '',
              status: '',
              searchValue: ''
            });
            setSelectedJobSageId('');
          } else {
            setPage(0);
            setFilters({
              ...filters,
              vendorSageId: vendorSageId || undefined,
              subType: selectedSubType || undefined,
              status: selectedStatus || undefined,
              searchValue: searchValue || undefined
            });
          }
        }}
        setSearchValue={setSearchValue}
        searchValue={searchValue}
        setSelectedSubType={setSelectedSubType}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        loadingVendorSubTypes={loadingVendorSubTypes}
        vendorSubTypes={vendorSubTypes}
        isLoadingStatuses={isLoadingStatuses}
        statuses={statuses}
        selectedSubType={selectedSubType}
        setJobName={setJobName}
        jobName={jobName}
      />
      <Table
        columns={invoicesColumns}
        data={invoices}
        isLoading={isLoadingInvoices || loadingVendors}
        hidePagination
        ResponsiveComponent={isMobile ? InvoicesMobile : undefined}
        mobileProps={{ isAdmin: isAdmin }}
      />
      {!isLoadingInvoices && !loadingVendors && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </VendorPage>
  );
};
